import { scaleLinear, scaleQuantize } from 'd3-scale';
import { area, curveStepAfter, line } from 'd3-shape';

import { hoursOfDay } from './AGPGraph';
import { nameKey } from '../utils/dataUtils';
import './Dashboard.css';

function profileGenerator({ x, y }) {
  const generator = area()
    .x(x)
    .y0(y(0))
    .y1(d => (y(d.value)))
    .curve(curveStepAfter);
  return function profilePath(d) {
    return generator([...d, { time: '24:00', value: d[d.length - 1].value }]);
  };
}

// Takes in a string e.g '05:00'
// Returns: a number
function parseMoment(d) {
  const { time } = d;
  const parts = time.split(':');
  const moment = +parts[0] + (+parts[1] / 60);
  return moment;
}

function getProfileTitleGenerator(t) {
  return function (p) {
    return p.profile.basal.map((step) => `${step.time}: ${step.value} ${t('insulin.iu/h')}`).join('\n');
  };
}

function render(selection) {
  const {
    data = {},
    profile,
    maxYValue,
    index,
    opacity,
    labels,
    color,
    graphHeight,
    t,
  } = selection.datum();
  const dailyEntries = Object.values(data);
  const {
    hours = hoursOfDay,
    width = 720,
    height = graphHeight,
  } = selection.datum();


  const hourWidth = width / hours.length;

  const profileExtent = [0, 24];
  const timeScale = scaleLinear()
    .domain(profileExtent)
    .range(profileExtent.map(h => (h * hourWidth)));

  const maxBasal = maxYValue;

  const y = scaleLinear().domain([0, maxBasal]).range([height, 0]);
  renderProfile({
    selection, profile, index, labels, opacity, color, dailyEntries, timeScale, y, t
  });
}

function renderProfile(props) {
  const {
    selection,
    timeScale,
    profile,
    opacity,
    labels,
    color,
    y,
    inTransition,
    t,
  } = props;

  const x = step => timeScale(parseMoment(step));
  const graphsG = selection
    .selectAll('g.graphs')
    .data([1])
    .join('g')
    .attr('class', 'profile graphs');

  // X and Y are functions, calculted with timescale
  const profilePathGenerator = profileGenerator({ x, y });
  const profileTitleGenerator = getProfileTitleGenerator(t);

  const basalArray = profile.profile.basal;
  let editedProfile = profile;
  editedProfile.profile.basal = basalArray;
  const profileBars = graphsG.selectAll('path.profile')
    .data([editedProfile], nameKey)
    .join(
      (enter) => {
        const path = enter.insert('path')
          .attr('class', 'profile')
          .attr('shape-rendering', 'auto')
          .style('stroke', 'none')
          .style('opacity', opacity)
          .style('fill', color)
          .attr('d', d => profilePathGenerator(d.profile.basal));
        path.append('title')
        .text(profileTitleGenerator);
        return path;
      },
      (update) => {
        const t = update
          .transition(inTransition)
          .attr('d', d => profilePathGenerator(d.profile.basal));
        t.select('title');
        return t;
      },
      exit => exit.remove(),
    );


  const width = 720;
  const height = 170;
  const timeScaleHours = scaleLinear()
    .domain([0, 24])
    .range([0, width]);

  if (labels) {
    graphsG.selectAll('text')
      .data(profile.profile.basal)
      .join('text')
      .attr('class', 'basalStepText')
      .style('stroke-width', '0.5')
      .style('text-anchor', 'middle')
      .style('stroke', 'none')
      .style('font', 'bold 0.9rem sans-serif')
      .style('fill', 'white')
      .text(function (d, i, a) {
        return d.value;
      })
      .attr('dx', function (d, i, a) {
        if (i +1 >= a.length) {
          return timeScaleHours((parseMoment(d) + 24) / 2);
        }
        return (timeScaleHours(parseMoment(d)) + timeScaleHours(parseMoment(a[i + 1].__data__))) / 2;
      })
      .attr('dy', height - 5);
  }

}

const BasalGraph = {
  init: f => f,
  render,
  destroy: f => f,
};

export default BasalGraph;
